import { BeforeMount, Editor, OnMount } from "@monaco-editor/react";
import { Box, Button, Stack } from "@mui/material";
import { usePlatformTheme } from "app/providers/PlatformThemeProvider";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type * as monacoType from "monaco-editor";
import { applyMonacoPlatformTheme } from "modules/record/record-producer/components/record-producer-fields/code-editor/applyMonacoPlatformTheme";

interface HtmlEditorProps {
    value: string;
    dirty: boolean;
    onChange: (value: string) => void;
    onSync: () => void;
}

const HtmlEditor = ({ value, dirty, onChange, onSync }: HtmlEditorProps) => {
    const { t } = useTranslation();
    const { activeTheme } = usePlatformTheme();
    const [monacoInstance, setMonacoInstance] = useState<typeof monacoType | null>(null);

    const beforeMount: BeforeMount = useCallback((monaco) => {
        applyMonacoPlatformTheme(monaco, activeTheme);
    }, [activeTheme]);

    const handleMount: OnMount = useCallback((_editor, monaco: typeof monacoType) => {
        setMonacoInstance(monaco);
        applyMonacoPlatformTheme(monaco, activeTheme);
        monaco.languages.html.htmlDefaults.setModeConfiguration({
            ...monaco.languages.html.htmlDefaults.modeConfiguration,
            diagnostics: true,
        });
    }, [activeTheme]);

    useEffect(() => {
        if (!monacoInstance) return;
        applyMonacoPlatformTheme(monacoInstance, activeTheme);
    }, [activeTheme, monacoInstance]);

    return (
        <Box className="service-builder__developer-editor">
            <Stack direction="row" justifyContent="flex-end" className="service-builder__developer-actions">
                <Button size="small" variant="contained" disabled={!dirty} onClick={onSync}>
                    {t("service_builder.sync_to_no_code", { defaultValue: "Sync to No-Code" })}
                </Button>
            </Stack>
            <Box className="service-builder__monaco-frame">
                <Editor
                    height="100%"
                    language="html"
                    path="file:///primum/service-portal/form-template.html"
                    theme="primum-platform-editor"
                    beforeMount={beforeMount}
                    value={value}
                    onChange={(nextValue) => onChange(nextValue || "")}
                    onMount={handleMount}
                    options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        automaticLayout: true,
                        scrollBeyondLastLine: false,
                        wordWrap: "on",
                    }}
                />
            </Box>
        </Box>
    );
};

export default HtmlEditor;
